import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { formatQuoteMoney } from '../../utils/quotePricing';
import { ledgerFromPayments } from '../../utils/moneyPresentation';
import { paymentStateLabel, studioDate } from '../../utils/studioDate';

interface QuoteRow {
  id: string;
  quoteNumber: string;
  status: string;
  client?: { name?: string };
  archivedAt?: string | null;
  deletedAt?: string | null;
  isTest?: boolean;
}

interface Payment {
  id: string;
  type: string;
  status: string;
  verifiedMinor: number;
  paidAt?: string | null;
  reference?: string | null;
}

interface Summary {
  quoteId: string;
  quoteNumber: string;
  lockedMinor: number;
  receivedMinor: number;
  outstandingMinor: number;
  currentlyDueMinor?: number;
  depositDueMinor?: number;
  figures?: { overdueMinor?: number; paidInFull?: boolean };
  payments: Payment[];
}

interface Accounting {
  invoiceCaptured?: boolean;
  invoiceReference?: string;
  payments?: Array<{ paymentId: string; captured: boolean; zohoReference: string }>;
}

const activeQuote = (quote?: QuoteRow) => Boolean(quote && !quote.archivedAt && !quote.deletedAt && !quote.isTest);

const StudioPayments = ({ focus = '', onOpenShoot }: { focus?: string; onOpenShoot?: (quoteId: string) => void }) => {
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [summaries, setSummaries] = useState<Summary[]>([]);
  const [accounting, setAccounting] = useState<Record<string, Accounting>>({});
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState(focus || 'all');
  const [scope, setScope] = useState<'operational' | 'historical'>('operational');
  const [selected, setSelected] = useState<Summary | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [quoteResponse, paymentResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/quotes`),
          fetch(`${API_BASE_URL}/billing/payments`),
        ]);
        if (!quoteResponse.ok || !paymentResponse.ok) throw new Error('Payments could not be loaded.');
        const quoteBody = await quoteResponse.json();
        const paymentBody = await paymentResponse.json();
        if (!active) return;
        setQuotes(Array.isArray(quoteBody) ? quoteBody : []);
        setSummaries(Array.isArray(paymentBody) ? paymentBody : []);
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : 'Payments could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (focus) setFilter(focus);
  }, [focus]);

  const byId = new Map(quotes.map((quote) => [quote.id, quote]));
  const recordMarks = (quote?: QuoteRow) => [quote?.deletedAt ? 'Recently deleted' : '', quote?.archivedAt ? 'Archived' : '', quote?.isTest ? 'Test' : ''].filter(Boolean).join(' · ');
  const inScope = summaries.filter((summary) => scope === 'historical' || activeQuote(byId.get(summary.quoteId)));
  const totals = inScope.reduce((total, summary) => {
    const ledger = ledgerFromPayments(summary.payments || [], summary.receivedMinor || 0);
    return {
      gross: total.gross + ledger.grossMinor,
      refunded: total.refunded + ledger.refundedMinor,
      net: total.net + ledger.netMinor,
      outstanding: total.outstanding + summary.outstandingMinor,
      due: total.due + (summary.currentlyDueMinor || 0),
      overdue: total.overdue + (summary.figures?.overdueMinor || 0),
    };
  }, { gross: 0, refunded: 0, net: 0, outstanding: 0, due: 0, overdue: 0 });

  const ledgerRows = useMemo(() => inScope.flatMap((summary) => (summary.payments || []).map((payment) => ({
    ...payment,
    summary,
    quote: byId.get(summary.quoteId),
  }))), [inScope, quotes]);

  const filteredSummaries = inScope.filter((summary) => {
    const quote = byId.get(summary.quoteId);
    const needle = query.trim().toLowerCase();
    const name = `${quote?.client?.name || ''} ${summary.quoteNumber}`.toLowerCase();
    if (needle && !name.includes(needle)) return false;
    if (filter === 'overdue') return (summary.figures?.overdueMinor || 0) > 0;
    if (filter === 'deposit') return (summary.depositDueMinor || 0) > 0;
    if (filter === 'paid') return Boolean(summary.figures?.paidInFull);
    if (filter === 'outstanding') return summary.outstandingMinor > 0;
    if (filter === 'due') return (summary.currentlyDueMinor || 0) > 0;
    if (filter === 'received') return (summary.payments || []).some((payment) => payment.status === 'complete');
    return true;
  });
  const visibleLedger = filter === 'all'
    ? ledgerRows
    : ledgerRows.filter((row) => filteredSummaries.some((summary) => summary.quoteId === row.summary.quoteId));

  const openAccounting = async (summary: Summary) => {
    setSelected(summary);
    if (accounting[summary.quoteId]) return;
    const response = await fetch(`${API_BASE_URL}/billing/quotes/${summary.quoteId}/accounting`);
    if (!response.ok) return;
    const body = await response.json();
    setAccounting((current) => ({ ...current, [summary.quoteId]: body }));
  };

  const saveZoho = async (summary: Summary, paymentId: string, captured: boolean, zohoReference = '') => {
    const current = accounting[summary.quoteId] || { payments: [] };
    const response = await fetch(`${API_BASE_URL}/billing/quotes/${summary.quoteId}/accounting`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invoiceCaptured: Boolean(current.invoiceCaptured),
        invoiceReference: current.invoiceReference || '',
        payments: [{ paymentId, captured, zohoReference }],
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body.error || 'Zoho could not be updated.');
      return;
    }
    if (body.paymentsUnchanged === false) {
      setError('The payment record changed unexpectedly. Reload before continuing.');
      return;
    }
    setAccounting((existing) => ({ ...existing, [summary.quoteId]: body }));
  };

  return (
    <div className="studio-home">
      <header className="studio-home-head">
        <div>
          <h2 className="studio-duplicate">Payments</h2>
          <p>Verified receipts stay on the PayFast record. Zoho ticks do not change them.</p>
        </div>
      </header>
      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {loading ? <p className="studio-note">Loading payments…</p> : (
      <section>
        <h3 className="studio-section-label">{scope === 'historical' ? 'Financial history' : 'Active accepted quotations'}</h3>
        <p className="studio-note">{scope === 'historical' ? 'Includes archived, recently deleted and test quotations. These totals are separate from the active studio totals.' : 'Archived, recently deleted and test quotations are excluded from these totals.'}</p>
        <div className="studio-money studio-money-three">
          <button type="button" onClick={() => setFilter('received')}><small>Paid so far</small><strong>{formatQuoteMoney(totals.net, 'R')}</strong></button>
          <button type="button" onClick={() => setFilter('outstanding')}><small>Still to pay</small><strong>{formatQuoteMoney(totals.outstanding, 'R')}</strong></button>
          <button type="button" onClick={() => setFilter('overdue')}><small>Overdue</small><strong>{formatQuoteMoney(totals.overdue, 'R')}</strong></button>
        </div>
        <p className="studio-note">Paid so far is after refunds ({formatQuoteMoney(totals.gross, 'R')} received, {formatQuoteMoney(totals.refunded, 'R')} refunded).</p>      </section>
      )}
      <div className="studio-toolbar">
        <input aria-label="Search payments" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search client or reference" />
        <select aria-label="Reporting mode" value={scope} onChange={(event) => setScope(event.target.value as 'operational' | 'historical')}>
          <option value="operational">Active studio</option>
          <option value="historical">Financial history</option>
        </select>
        <select aria-label="Payment filter" value={filter} onChange={(event) => setFilter(event.target.value)}>
          <option value="all">All balances</option>
          <option value="outstanding">Still to pay</option>
          <option value="overdue">Overdue</option>
          <option value="deposit">Deposit not met</option>
          <option value="paid">Paid in full</option>
        </select>
      </div>
      <h3 className="studio-section-label">Client balances</h3>
      <div className="studio-table-wrap">
        <table className="studio-table">
          <thead>
            <tr><th>Client</th><th>Quotation</th><th>Status</th><th>Paid so far</th><th>Still to pay</th><th>Overdue</th><th></th></tr>
          </thead>
          <tbody>
            {filteredSummaries.length === 0 ? <tr><td colSpan={7}>No balances match this filter.</td></tr> : filteredSummaries.map((summary) => {
              const overdue = (summary.figures?.overdueMinor || 0) > 0;
              const quote = byId.get(summary.quoteId);
              const statusLine = overdue
                ? 'Overdue'
                : (summary.figures?.paidInFull ? 'Paid in full' : (summary.depositDueMinor || 0) > 0 ? 'Deposit outstanding' : summary.outstandingMinor > 0 ? 'Balance outstanding' : 'Settled');
              return (
              <tr
                key={summary.quoteId}
                style={{ cursor: onOpenShoot ? 'pointer' : undefined }}
                data-testid={`payment-row-${summary.quoteId}`}
                onClick={() => onOpenShoot?.(summary.quoteId)}
              >
                <td data-label="Client">{quote?.client?.name || 'Client'}{scope === 'historical' && recordMarks(quote) ? ` · ${recordMarks(quote)}` : ''}</td>
                <td data-label="Reference">{summary.quoteNumber}</td>
                <td data-label="Status"><span className={overdue || (summary.outstandingMinor || 0) > 0 ? 'studio-your-move' : 'studio-waiting'}>{statusLine}</span></td>
                <td data-label="Paid so far">{formatQuoteMoney(summary.receivedMinor || 0, 'R')}</td>
                <td data-label="Still to pay">{formatQuoteMoney(summary.outstandingMinor, 'R')}</td>
                <td data-label="Overdue">{overdue ? formatQuoteMoney(summary.figures?.overdueMinor || 0, 'R') : '—'}</td>
                <td data-label="Action" onClick={(event) => event.stopPropagation()}>
                  <button type="button" className="studio-text-action" onClick={() => openAccounting(summary)}>Zoho</button>
                  {onOpenShoot ? (
                    <>
                      {' '}
                      <button type="button" className="studio-text-action" onClick={() => onOpenShoot(summary.quoteId)}>Open shoot</button>
                    </>
                  ) : null}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <h3 className="studio-section-label">Transactions</h3>
      <div className="studio-table-wrap">
        <table className="studio-table">
          <thead>
            <tr><th>Date</th><th>Client</th><th>Amount</th><th>Type</th><th>Verification</th><th>Zoho</th></tr>
          </thead>
          <tbody>
            {visibleLedger.length === 0 ? <tr><td colSpan={6}>No payments recorded.</td></tr> : visibleLedger.map((row) => (
              <tr key={row.id}>
                <td data-label="Date">{row.paidAt ? studioDate(row.paidAt) : ''}</td>
                <td data-label="Client">{row.quote?.client?.name || 'Client'}{scope === 'historical' && recordMarks(row.quote) ? ` · ${recordMarks(row.quote)}` : ''}</td>
                <td data-label="Amount">{formatQuoteMoney(row.verifiedMinor || 0, 'R')}</td>
                <td data-label="Type">{row.type === 'refund' ? 'Refund' : 'Receipt'}</td>
                <td data-label="Verification">{paymentStateLabel(row.type, row.status)}</td>
                <td data-label="Zoho">{accounting[row.summary.quoteId] ? ((accounting[row.summary.quoteId]?.payments || []).find((item) => item.paymentId === row.id)?.captured ? 'Recorded in Zoho' : 'Not recorded') : 'Open details'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selected && (
        <section className="studio-detail">
          <div>
            <h3>{byId.get(selected.quoteId)?.client?.name || 'Client'}</h3>
            <p>{selected.quoteNumber}</p>
            <p>Still to pay {formatQuoteMoney(selected.outstandingMinor, 'R')}</p>
          </div>
          <div>
            <h3 className="studio-section-label">Zoho</h3>
            <p>Invoice {accounting[selected.quoteId]?.invoiceCaptured ? 'captured' : 'not captured'} {accounting[selected.quoteId]?.invoiceReference || ''}</p>
            {(selected.payments || []).filter((payment) => payment.status === 'complete' && payment.type !== 'refund').map((payment) => {
              const capture = (accounting[selected.quoteId]?.payments || []).find((item) => item.paymentId === payment.id);
              return (
                <form key={payment.id} className="studio-toolbar" onSubmit={(event) => {
                  event.preventDefault();
                  const reference = new FormData(event.currentTarget).get('zohoReference');
                  saveZoho(selected, payment.id, true, String(reference || capture?.zohoReference || ''));
                }}>
                  <p>{paymentStateLabel(payment.type, payment.status)} · {formatQuoteMoney(payment.verifiedMinor || 0, 'R')}</p>
                  <label>
                    <input
                      type="checkbox"
                      checked={Boolean(capture?.captured)}
                      onChange={(event) => {
                        const reference = (event.currentTarget.form?.elements.namedItem('zohoReference') as HTMLInputElement | null)?.value || '';
                        saveZoho(selected, payment.id, event.target.checked, reference);
                      }}
                    />
                    {' '}Record in Zoho
                  </label>
                  <input name="zohoReference" aria-label="Zoho reference" defaultValue={capture?.zohoReference || ''} placeholder="Zoho reference" />
                  <button type="submit" className="studio-secondary">Save Zoho reference</button>
                </form>
              );
            })}
            {(selected.payments || []).filter((payment) => payment.status === 'complete' && payment.type !== 'refund').length === 0 ? <p>No verified receipt is waiting to be recorded.</p> : null}
            <p className="studio-note">Marking Zoho does not change the verified PayFast amount.</p>
          </div>
        </section>
      )}
    </div>
  );
};

export default StudioPayments;
